import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import {
  Badge, Breadcrumbs, Card, Empty, ErrorNotice, Notice, Tile, fmt, useToast,
} from '../components/ui.jsx';

const STEPS = ['Choose target', 'Upload file', 'Map columns', 'Match and review'];

export default function UploadPage() {
  const { can } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInput = useRef(null);

  const [step, setStep] = useState(0);
  const [target, setTarget] = useState('ic_master');
  const [insurerId, setInsurerId] = useState('');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [upload, setUpload] = useState(null);      // response of step 2
  const [columnMap, setColumnMap] = useState({});
  const [stageResult, setStageResult] = useState(null);
  const [error, setError] = useState(null);

  const insurers = useQuery({ queryKey: ['insurers'], queryFn: () => api.listInsurers().then((r) => r.data) });

  const uploadMutation = useMutation({
    mutationFn: async () => {
      const form = new FormData();
      form.append('file', file);
      form.append('target', target);
      if (target === 'ic_master') form.append('insurerId', insurerId);
      return api.uploadMaster(form).then((r) => r.data);
    },
    onSuccess: (data) => {
      setUpload(data);
      setColumnMap(data.suggestedMap || {});
      setError(null);
      setStep(2);
    },
    onError: setError,
  });

  const stageMutation = useMutation({
    mutationFn: () => api.stageBatch(upload.batchId, columnMap, true).then((r) => r.data),
    onSuccess: (data) => {
      setStageResult(data);
      setError(null);
      setStep(3);
      queryClient.invalidateQueries({ queryKey: ['overview'] });
      toast.success('Rows staged. The matching engine is running.');
    },
    onError: setError,
  });

  // While the match run is in progress, poll the batch for progress.
  const batchStatus = useQuery({
    queryKey: ['batch', upload?.batchId],
    queryFn: () => api.getBatch(upload.batchId).then((r) => r.data),
    enabled: step === 3 && Boolean(upload?.batchId),
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === 'completed' || s === 'failed' ? false : 2000;
    },
  });

  useEffect(() => {
    if (batchStatus.data?.status === 'completed') {
      queryClient.invalidateQueries({ queryKey: ['overview'] });
      queryClient.invalidateQueries({ queryKey: ['mappings'] });
    }
  }, [batchStatus.data?.status, queryClient]);

  const requiredFields = useMemo(
    () => (upload?.canonicalFields || []).filter((f) => f.required),
    [upload],
  );
  const unmappedRequired = requiredFields.filter((f) => !columnMap[f.key]);

  const pickFile = useCallback((f) => {
    if (!f) return;
    const ok = /\.(xlsx|xls|csv)$/i.test(f.name);
    if (!ok) { setError({ message: `"${f.name}" is not an .xlsx, .xls or .csv file` }); return; }
    setError(null);
    setFile(f);
  }, []);

  const reset = () => {
    setStep(0); setFile(null); setUpload(null); setColumnMap({});
    setStageResult(null); setError(null);
  };

  if (!can('reviewer')) {
    return (
      <div className="page">
        <Breadcrumbs items={[{ label: 'Upload master' }]} />
        <Notice tone="warn" title="You do not have permission to import masters">
          Importing requires the reviewer or admin role. Ask an administrator to change your role.
        </Notice>
      </div>
    );
  }

  return (
    <div className="page">
      <Breadcrumbs items={[{ label: 'Dashboard', to: '/dashboard' }, { label: 'Upload master' }]} />
      <div className="page-head">
        <div>
          <h1>Upload a master file</h1>
          <div className="page-sub">
            Load an insurer master to map it against the GIIB master, or extend the GIIB master
            itself with a bulk file of new vehicles.
          </div>
        </div>
        <div className="spacer" />
        {step > 0 && <button className="btn" onClick={reset}>Start over</button>}
      </div>

      <div className="steps">
        {STEPS.map((label, i) => (
          <div key={label} className={`step ${i === step ? 'active' : ''} ${i < step ? 'done' : ''}`}>
            <span className="step-no">{i < step ? '✓' : i + 1}</span>
            {label}
          </div>
        ))}
      </div>

      <ErrorNotice error={error} />

      {/* ---------------------------- step 1 ---------------------------- */}
      {step === 0 && (
        <Card title="What are you importing?">
          <div className="field">
            <label htmlFor="target">Target</label>
            <select id="target" value={target} onChange={(e) => setTarget(e.target.value)}>
              <option value="ic_master">Insurer master - map insurer codes to GIIB vehicles</option>
              <option value="giib_master">GIIB master - add new vehicles and mint universal keys</option>
            </select>
            <div className="field-hint">
              {target === 'ic_master'
                ? 'Every row is matched against the GIIB master. Nothing is written to the GIIB master itself.'
                : 'Every row becomes a GIIB vehicle with a new universal key. Near-duplicates are skipped, not merged.'}
            </div>
          </div>

          {target === 'ic_master' && (
            <div className="field">
              <label htmlFor="insurer">Insurance company</label>
              <select id="insurer" value={insurerId} onChange={(e) => setInsurerId(e.target.value)}>
                <option value="">Select an insurer...</option>
                {(insurers.data || []).map((i) => (
                  <option key={i.id} value={i.id}>{i.name} ({i.shortCode})</option>
                ))}
              </select>
              <div className="field-hint">
                Not listed? <Link to="/insurers">Register the insurer</Link> first - it takes effect immediately,
                no configuration change is needed.
              </div>
            </div>
          )}

          <div className="btn-row">
            <button
              className="btn btn-primary"
              disabled={target === 'ic_master' && !insurerId}
              onClick={() => setStep(1)}
            >
              Continue
            </button>
          </div>
        </Card>
      )}

      {/* ---------------------------- step 2 ---------------------------- */}
      {step === 1 && (
        <Card title="Select the file" subtitle="Accepted formats: .xlsx, .xls, .csv. The first row must contain column headers.">
          <div
            className={`dropzone ${dragOver ? 'over' : ''}`}
            onClick={() => fileInput.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0]); }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter') fileInput.current?.click(); }}
          >
            <input
              ref={fileInput} type="file" accept=".xlsx,.xls,.csv" hidden
              onChange={(e) => pickFile(e.target.files?.[0])}
            />
            {file ? (
              <>
                <div style={{ fontWeight: 600 }}>{file.name}</div>
                <div className="prov" style={{ marginTop: 4 }}>
                  {(file.size / 1024 / 1024).toFixed(2)} MB - click to choose a different file
                </div>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 600 }}>Drop the master file here, or click to browse</div>
                <div className="prov" style={{ marginTop: 4 }}>Maximum 64 MB</div>
              </>
            )}
          </div>

          <div className="btn-row" style={{ marginTop: 16 }}>
            <button className="btn" onClick={() => setStep(0)}>Back</button>
            <button
              className="btn btn-primary"
              disabled={!file || uploadMutation.isPending}
              onClick={() => uploadMutation.mutate()}
            >
              {uploadMutation.isPending ? 'Uploading...' : 'Upload and read columns'}
            </button>
          </div>
        </Card>
      )}

      {/* ---------------------------- step 3 ---------------------------- */}
      {step === 2 && upload && (
        <>
          {upload.previouslyImported && (
            <Notice tone="warn" title="This exact file has been imported before">
              Batch #{upload.previouslyImported.batchId} ({upload.previouslyImported.filename}) had the same
              checksum. Re-importing is safe - existing insurer codes are updated in place, not duplicated.
            </Notice>
          )}

          <div className="tiles">
            <Tile label="File" value={upload.filename.length > 22 ? `${upload.filename.slice(0, 20)}...` : upload.filename} foot={`Batch #${upload.batchId}`} />
            <Tile label="Data rows" value={fmt(upload.rowCount)} foot={`${upload.headers.length} columns detected`} />
            <Tile
              label="Auto-mapped fields"
              value={Object.keys(upload.suggestedMap || {}).length}
              foot={`of ${upload.canonicalFields.length} available`}
              tone="accent"
            />
          </div>

          <Card
            title="Map the file's columns to MMV fields"
            subtitle="Fields marked required drive the match. Anything left unmapped is simply not used in scoring."
          >
            {unmappedRequired.length > 0 && (
              <Notice tone="warn">
                Still to map: {unmappedRequired.map((f) => f.label).join(', ')}
              </Notice>
            )}

            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th style={{ width: '32%' }}>MMV field</th>
                    <th style={{ width: '38%' }}>Column in your file</th>
                    <th>Sample value</th>
                  </tr>
                </thead>
                <tbody>
                  {upload.canonicalFields.map((f) => (
                    <tr key={f.key}>
                      <td>
                        <div className="cell-main">
                          {f.label} {f.required && <Badge kind="fail">required</Badge>}
                        </div>
                        <div className="prov">{f.key}</div>
                      </td>
                      <td>
                        <select
                          value={columnMap[f.key] || ''}
                          onChange={(e) => {
                            const next = { ...columnMap };
                            if (e.target.value) next[f.key] = e.target.value; else delete next[f.key];
                            setColumnMap(next);
                          }}
                          aria-label={`Column for ${f.label}`}
                        >
                          <option value="">Not mapped</option>
                          {upload.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                        </select>
                      </td>
                      <td className="prov">
                        {columnMap[f.key]
                          ? `read from column "${columnMap[f.key]}"`
                          : 'no column selected'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="btn-row" style={{ marginTop: 16 }}>
              <button className="btn" onClick={() => setStep(1)}>Back</button>
              <button
                className="btn btn-primary"
                disabled={unmappedRequired.length > 0 || stageMutation.isPending}
                onClick={() => stageMutation.mutate()}
              >
                {stageMutation.isPending ? 'Staging rows...' : 'Stage rows and run matching'}
              </button>
            </div>
          </Card>
        </>
      )}

      {/* ---------------------------- step 4 ---------------------------- */}
      {step === 3 && stageResult && (
        <>
          <div className="tiles">
            <Tile label="Rows read" value={fmt(stageResult.total)} />
            <Tile label="New records" value={fmt(stageResult.inserted)} tone="accent" />
            <Tile label="Updated records" value={fmt(stageResult.updated ?? stageResult.duplicates ?? 0)} foot={stageResult.updated !== undefined ? 'Existing codes refreshed' : 'Duplicates skipped'} />
            <Tile label="Rows skipped" value={fmt(stageResult.skipped)} tone={stageResult.skipped ? 'warn' : ''} />
          </div>

          {stageResult.errors?.length > 0 && (
            <Notice tone="warn" title={`${stageResult.errors.length} row(s) could not be staged`}>
              <ul>
                {stageResult.errors.slice(0, 10).map((e, i) => (
                  <li key={i}>Row {e.row}: {e.reason}</li>
                ))}
              </ul>
              {stageResult.errors.length > 10 && <div className="prov">Showing the first 10.</div>}
            </Notice>
          )}

          <Card title="Matching run" subtitle="The engine scores every staged row against the GIIB master.">
            {stageResult.target === 'giib_master' ? (
              <Notice tone="ok" title="GIIB master updated">
                {fmt(stageResult.inserted)} vehicles were added and given universal keys.
                Upload an insurer master next to link insurer codes to them.
              </Notice>
            ) : batchStatus.isLoading ? (
              <div className="btn-row"><div className="spinner" /> Starting...</div>
            ) : batchStatus.data ? (
              <>
                <div className="tiles">
                  <Tile label="Status" value={<Badge kind={batchStatus.data.status === 'completed' ? 'approved' : batchStatus.data.status === 'failed' ? 'rejected' : 'pending'}>{batchStatus.data.status}</Badge>} />
                  <Tile label="Processed" value={`${fmt(batchStatus.data.processedRows)} / ${fmt(batchStatus.data.totalRows)}`} />
                  <Tile label="Auto-approved" value={fmt(batchStatus.data.autoMatched)} tone="ok" />
                  <Tile label="Needs review" value={fmt(batchStatus.data.needsReview)} tone="warn" />
                  <Tile label="No match" value={fmt(batchStatus.data.unmatched)} tone={batchStatus.data.unmatched ? 'danger' : ''} />
                </div>

                {batchStatus.data.status === 'failed' && (
                  <Notice tone="error" title="The match run failed">{batchStatus.data.errorMessage}</Notice>
                )}
                {batchStatus.data.status === 'completed' && (
                  <Notice tone="ok" title="Match run complete">
                    Auto-approved rows are already live in the registry. Everything else is waiting in the
                    review queue.
                  </Notice>
                )}

                <div className="btn-row">
                  <button className="btn btn-primary" onClick={() => navigate('/mappings')}>
                    Open review queue
                  </button>
                  <button className="btn" onClick={() => navigate(`/imports`)}>Import history</button>
                  <button className="btn btn-ghost" onClick={reset}>Import another file</button>
                </div>
              </>
            ) : <Empty title="No status available" />}
          </Card>
        </>
      )}
    </div>
  );
}
